import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { Loader2, Lock } from 'lucide-react';
import { useExportFields, useExportTemplates, type ExportTemplate } from '@/lib/export';
import { TemplateList } from '@/features/export/template-list';
import { TemplateBuilder } from '@/features/export/template-builder';

export const Route = createFileRoute('/settings/export')({
  beforeLoad: () => {
    throw redirect({ to: '/settings', search: { tab: 'export' } });
  },
});

type View = { mode: 'list' } | { mode: 'edit'; template?: ExportTemplate };

export function ExportPage() {
  const [view, setView] = useState<View>({ mode: 'list' });
  const fieldsQuery = useExportFields();
  const templatesQuery = useExportTemplates();

  if (fieldsQuery.isLoading || templatesQuery.isLoading) {
    return (
      <div className="flex h-[calc(100vh-7rem)] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fieldsQuery.isError) {
    const status = (fieldsQuery.error as { response?: { status?: number } })?.response?.status;
    return (
      <div className="flex h-[calc(100vh-7rem)] flex-col items-center justify-center px-6 text-center">
        <div className="mb-4 rounded-2xl bg-muted p-4">
          <Lock className="h-8 w-8 text-muted-foreground" />
        </div>
        <h2 className="text-base font-medium text-foreground">
          {status === 403 ? 'No access to exports' : 'Could not load the exporter'}
        </h2>
        <p className="mt-1 max-w-xs text-sm text-muted-foreground">
          {status === 403
            ? 'You need the “Run data exports” permission. Ask an administrator to grant it.'
            : 'Something went wrong loading export fields. Please try again.'}
        </p>
      </div>
    );
  }

  const groups = fieldsQuery.data ?? [];
  const templates = templatesQuery.data ?? [];

  if (view.mode === 'edit') {
    return (
      <TemplateBuilder
        template={view.template}
        groups={groups}
        onDone={() => setView({ mode: 'list' })}
      />
    );
  }

  return (
    <TemplateList
      templates={templates}
      onNew={() => setView({ mode: 'edit' })}
      onEdit={(template) => setView({ mode: 'edit', template })}
    />
  );
}
